import React, { useState } from "react";

import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { Grid } from "@material-ui/core";
import { useSelector } from "react-redux";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import {
  Button,
  CollapseHeader,
  FormCollapse,
  SecondaryButton,
} from "components/Form";
import { SmallFaIcon } from "components/FaIcon";
import { Row } from "components/Layout";
import { Text } from "components/Text";

import { EContractType } from "constants/eContract";

import { EContractSpecialConditionsEditor } from "./eContractSpecialConditionsEditor";
import { EContractDataReviewSection } from "./ReviewSections";
import { getActiveLivestockAgentDeployment } from "selectors";

interface EContractConditionsModalProps {
  agreement: EContractAgreement;
  conditions: EContractSpecialCondition[];
  onClose: () => void;
  onGenerateContract: () => void;
  readOnly?: boolean;
}

function getConditionsHelperText(
  eContractType: string | null | undefined,
): string | null {
  switch (eContractType) {
    case EContractType.ALPA:
      return '"Payment Arrangements" conditions appear in their own section on the contract. "Other" conditions appear in the Special Conditions block without a category prefix. All remaining conditions appear as "Category: description" in the Special Conditions block.';
    case EContractType.RMA:
      return '"Compliance" conditions appear in the NLIS section of the contract. "Other" conditions appear in the Special Conditions block without a category prefix. All remaining conditions appear as "Category: description" in the Special Conditions block.';
    default:
      return null;
  }
}

export const EContractConditionsModal = ({
  agreement,
  conditions,
  onClose,
  onGenerateContract,
  readOnly = false,
}: EContractConditionsModalProps): React.JSX.Element => {
  const [isConditionsOpen, setIsConditionsOpen] = useState(true);
  const [isAddingCondition, setIsAddingCondition] = useState(false);

  const deployment = useSelector(getActiveLivestockAgentDeployment);
  const eContractType = deployment?.deploymentSettings?.eContractType || null;

  const helperText = getConditionsHelperText(eContractType);
  const hasConditions =
    eContractType === EContractType.ALPA || eContractType === EContractType.RMA;

  const handleGenerate = () => {
    onClose();
    onGenerateContract();
  };

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle onClose={onClose}>Review eContract</DialogTitle>
      <DialogContent dividers form>
        <Grid item xs={12} style={{ marginTop: "12px", marginBottom: "12px" }}>
          {readOnly ? (
            <Row alignCenter gridGap={1}>
              <SmallFaIcon color="warning" icon={faCircleExclamation} />
              <Text italic color="titleMedium">
                The data shown here reflects the current sale data and may not
                match the drafted eContract if sale data has been updated since
                the contract was generated.
              </Text>
            </Row>
          ) : (
            <Text italic color="titleMedium">
              The data below will be populated in the eContract. <br /> Fields
              showing &ldquo;-&rdquo; are missing. You may wish to add them
              before drafting.
            </Text>
          )}
        </Grid>
        <Grid item xs={12}>
          <EContractDataReviewSection
            agreementId={agreement.id}
            eContractType={eContractType}
          />
        </Grid>
        {hasConditions && (
          <FormCollapse
            isOpen={isConditionsOpen}
            onToggle={() => setIsConditionsOpen(v => !v)}
            header={<CollapseHeader title="Special Conditions" />}
            scrollIntoView={false}
          >
            {helperText && (
              <Grid item xs={12}>
                <Text italic color="titleMedium">
                  {helperText}
                </Text>
              </Grid>
            )}
            <Grid item xs={12}>
              <EContractSpecialConditionsEditor
                agreementId={agreement.id}
                conditions={conditions}
                onFormActiveChange={readOnly ? undefined : setIsAddingCondition}
                readOnly={readOnly}
              />
            </Grid>
          </FormCollapse>
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton type="button" onClick={onClose}>
          Close
        </SecondaryButton>
        {!readOnly && (
          <Button
            type="button"
            onClick={handleGenerate}
            disabled={isAddingCondition}
          >
            Accept and Draft eContract
          </Button>
        )}
      </DialogActions>
    </ZoomyDialog>
  );
};
